"use client";

import { useState, useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

interface FormErrors {
  email?: string;
  petName?: string;
  submit?: string;
}

export default function OwnerSignupCTA() {
  const [email, setEmail] = useState("");
  const [petName, setPetName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ email: boolean; petName: boolean }>({
    email: false,
    petName: false,
  });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const validateEmail = (value: string): string | undefined => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return undefined;
  };

  const validatePetName = (value: string): string | undefined => {
    if (!value.trim()) return "Pet name is required";
    if (value.trim().length < 2) return "Pet name must be at least 2 characters";
    return undefined;
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (touched.email) {
      setErrors((prev) => ({ ...prev, email: validateEmail(value), submit: undefined }));
    }
  };

  const handlePetNameChange = (value: string) => {
    setPetName(value);
    if (touched.petName) {
      setErrors((prev) => ({ ...prev, petName: validatePetName(value), submit: undefined }));
    }
  };

  const handleBlur = (field: "email" | "petName") => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === "email") {
      setErrors((prev) => ({ ...prev, email: validateEmail(email) }));
    } else {
      setErrors((prev) => ({ ...prev, petName: validatePetName(petName) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailError = validateEmail(email);
    const petNameError = validatePetName(petName);

    setErrors({ email: emailError, petName: petNameError });
    setTouched({ email: true, petName: true });

    if (emailError || petNameError) return;

    setLoading(true);
    setErrors((prev) => ({ ...prev, submit: undefined }));

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, petName }),
        signal: AbortSignal.timeout(12000),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrors((prev) => ({
          ...prev,
          submit: data?.error || "Something went wrong. Please try again.",
        }));
        return;
      }

      setSubmitted(true);
    } catch (err: any) {
      if (err.name === "TimeoutError" || err.name === "AbortError") {
        setErrors((prev) => ({
          ...prev,
          submit: "Request timed out. Please check your connection and try again.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          submit: "Network error. Please check your connection and try again.",
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setErrors({});
    setTouched({ email: false, petName: false });
  };

  return (
    <section
      id="signup"
      ref={sectionRef}
      className="py-24 dark:bg-[#0f172a]"
      style={{ background: "linear-gradient(145deg, #E8F8F7 0%, #EEF2FF 100%)" }}
    >
      <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <div className="scroll-fade-up mb-4">
          <span className="teal-badge">
            <Icon name="RocketLaunchIcon" size={12} />
            Join the beta · Free forever plan
          </span>
        </div>
        <h2 className="scroll-fade-up text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight mt-4 mb-5">
          Ready to give{" "}
          <span className="gradient-text">your pet</span>
          <br />
          the best care?
        </h2>
        <p className="scroll-fade-up text-[#4A6363] dark:text-slate-400 text-lg mb-10 leading-relaxed">
          Create your pet's free profile in under 3 minutes. Join 2,400+ pet owners already in the DogFob community.
        </p>

        {submitted ? (
          <div className="scroll-fade-up feature-card p-10 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#E0F7F6] flex items-center justify-center animate-pulse-glow">
              <Icon name="CheckCircleIcon" size={32} className="text-teal-500" variant="solid" />
            </div>
            <h3 className="text-2xl font-extrabold text-[#1A2B2B] dark:text-slate-100 font-heading">You're on the list!</h3>
            <p className="text-[#4A6363] dark:text-slate-400">
              You're on the list! Check your inbox for a confirmation email.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Icon name="EnvelopeIcon" size={16} className="text-teal-500" />
              <span className="text-sm text-[#4A6363] dark:text-slate-400">Confirmation sent to <strong>{email}</strong></span>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="scroll-fade-up feature-card p-8 flex flex-col gap-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                  Your email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => handleBlur("email")}
                  placeholder="you@email.com"
                  disabled={loading}
                  className={`form-input ${
                    touched.email && errors.email
                      ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                      : touched.email && !errors.email
                      ? "border-green-400 focus:border-green-500 focus:ring-green-200" :""
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                  aria-label="Your email address"
                  aria-invalid={touched.email && !!errors.email}
                  aria-describedby={touched.email && errors.email ? "email-error" : undefined}
                />
                {touched.email && errors.email && (
                  <div id="email-error" className="flex items-center gap-1.5 text-xs text-red-600 mt-1">
                    <Icon name="ExclamationCircleIcon" size={14} />
                    <span>{errors.email}</span>
                  </div>
                )}
                {touched.email && !errors.email && email && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
                    <Icon name="CheckCircleIcon" size={14} />
                    <span>Looks good!</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1.5 text-left">
                <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                  Your pet's name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={petName}
                  onChange={(e) => handlePetNameChange(e.target.value)}
                  onBlur={() => handleBlur("petName")}
                  placeholder="e.g. Luna"
                  disabled={loading}
                  className={`form-input ${
                    touched.petName && errors.petName
                      ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                      : touched.petName && !errors.petName
                      ? "border-green-400 focus:border-green-500 focus:ring-green-200" :""
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                  aria-label="Your pet's name"
                  aria-invalid={touched.petName && !!errors.petName}
                  aria-describedby={touched.petName && errors.petName ? "petName-error" : undefined}
                />
                {touched.petName && errors.petName && (
                  <div id="petName-error" className="flex items-center gap-1.5 text-xs text-red-600 mt-1">
                    <Icon name="ExclamationCircleIcon" size={14} />
                    <span>{errors.petName}</span>
                  </div>
                )}
                {touched.petName && !errors.petName && petName && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
                    <Icon name="CheckCircleIcon" size={14} />
                    <span>Looks good!</span>
                  </div>
                )}
              </div>
            </div>

            {/* Submit error with retry */}
            {errors.submit && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-left">
                  <p className="text-sm text-red-700 font-medium">{errors.submit}</p>
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="text-xs text-red-600 underline mt-1 hover:text-red-800 transition-colors"
                  >
                    Dismiss and try again
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-4 text-base justify-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Joining waitlist...
                </>
              ) : (
                <>
                  <Icon name="UserPlusIcon" size={20} />
                  Create My Free Pet Profile
                </>
              )}
            </button>
            <p className="text-xs text-[#8AA4A4] dark:text-slate-500">
              No credit card required. Free plan includes unlimited pets.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}